import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { docsApi } from './docs.api.ts';
import { docsKeys } from './docs.keys.ts';
import type {
  DocInput,
  FeedbackInput,
  ReorderInput,
  SectionInput,
} from '../model/docs.type';

/** 편집 화면용 단건(초안 포함). */
export function useDocForEdit(id: string | undefined) {
  return useQuery({
    queryKey: [...docsKeys.articles(), 'edit', id ?? ''],
    queryFn: () => docsApi.getForEdit(id!),
    enabled: !!id,
    // 편집 화면은 항상 최신을 봐야 한다 — 캐시된 옛 본문 위에 저장하면 덮어쓴다.
    staleTime: 0,
    retry: false,
  });
}

/**
 * 문서·섹션이 바뀌면 **트리와 상세를 함께** 무효화한다.
 *
 * 트리에 제목·요약·경로가 들어 있어서 본문만 갱신하면 사이드바가 옛 제목을 유지하고,
 * 공개/관리 두 캐시(`manage` 플래그)가 따로 있어서 한쪽만 지우면 화면마다 다른 걸 본다.
 */
function useInvalidateDocs() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: docsKeys.all });
}

export function useCreateDoc() {
  const invalidate = useInvalidateDocs();
  return useMutation({
    mutationFn: (input: DocInput) => docsApi.createDoc(input),
    onSuccess: invalidate,
  });
}

export function useUpdateDoc() {
  const invalidate = useInvalidateDocs();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DocInput }) =>
      docsApi.updateDoc(id, input),
    onSuccess: invalidate,
  });
}

export function useRemoveDoc() {
  const invalidate = useInvalidateDocs();
  return useMutation({
    mutationFn: (id: string) => docsApi.removeDoc(id),
    onSuccess: invalidate,
  });
}

export function useCreateSection() {
  const invalidate = useInvalidateDocs();
  return useMutation({
    mutationFn: (input: SectionInput) => docsApi.createSection(input),
    onSuccess: invalidate,
  });
}

export function useUpdateSection() {
  const invalidate = useInvalidateDocs();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SectionInput }) =>
      docsApi.updateSection(id, input),
    onSuccess: invalidate,
  });
}

export function useRemoveSection() {
  const invalidate = useInvalidateDocs();
  return useMutation({
    mutationFn: (id: string) => docsApi.removeSection(id),
    onSuccess: invalidate,
  });
}

export function useReorderDocs() {
  const invalidate = useInvalidateDocs();
  return useMutation({
    mutationFn: (input: ReorderInput) => docsApi.reorder(input),
    onSuccess: invalidate,
  });
}

/**
 * 문서 하단 피드백 제출.
 *
 * 트리를 무효화하지 않는다 — 독자 화면에는 집계가 안 나오므로 다시 받을 이유가
 * 없고, 의견 하나 남길 때마다 트리를 통째로 다시 받는 건 낭비다. 관리 화면의
 * 숫자는 그 화면을 열 때 갱신된다.
 */
export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (input: FeedbackInput) => docsApi.submitFeedback(input),
  });
}

/** 관리 화면의 '아니오' 사유 목록. 펼칠 때만 받는다. */
export function useDocFeedback(docId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: docsKeys.feedback(docId ?? ''),
    queryFn: () => docsApi.getFeedback(docId!),
    enabled: !!docId && enabled,
    staleTime: 0,
  });
}
